import {
  signIn,
  signUp as amplifySignUp,
  resendSignUpCode,
  signOut as amplifySignOut,
  updatePassword,
  confirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  rememberDevice,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { getConfig } from 'config/configService';
import { sessionStorage as amplifyKeyValueStorage } from 'aws-amplify/utils';
import { persistTrustedDevice, restoreTrustedDeviceAsync } from 'utils/deviceKeyStorage';

const resolveAuthenticatorIssuer = (label) => {
  switch (String(label).toLowerCase()) {
    case 'prod':
    case 'production':
      return 'LendingUSA';
    case 'staging':
    case 'stage':
      return 'LendingUSA Staging';
    case 'dev':
    case 'development':
    default:
      return 'LendingUSA Dev';
  }
};

// Issuer shown in the authenticator app, derived from the configured env label.
// Resolved lazily (not at module load) because the deployment config is fetched
// asynchronously at startup and may not be populated when this module is imported.
const getAuthenticatorIssuer = () => resolveAuthenticatorIssuer(getConfig().envLabel);

/**
 * Build the otpauth:// setup URI for a TOTP association.
 *
 * Amplify's getSetupUri interpolates the issuer and account name into the URI
 * WITHOUT URL-encoding them (otpauth://totp/{issuer}:{account}?...&issuer={issuer}).
 * An issuer with a space (e.g. "LendingUSA Dev") or an account label that is an
 * email (containing "@") therefore produces a malformed URI that some
 * authenticator apps parse incorrectly — the app then generates codes that
 * Cognito rejects with "Code mismatch" on verifyTOTPSetup.
 *
 * Percent-encoding the issuer and account name before handing them to
 * getSetupUri yields a valid, unambiguous URI. This changes only the QR's label
 * text, never the shared secret, so scanning becomes reliable again.
 *
 * @param {object} setupDetails Amplify TOTP setup details exposing getSetupUri
 * @param {string} issuer the issuer/app name
 * @param {string} [accountName] the account label (optional)
 * @returns {string} the otpauth:// URI as a string
 */
const authenticatorIssuer = resolveAuthenticatorIssuer(getConfig().envLabel);
const buildTotpSetupUri = (setupDetails, issuer, accountName) => {
  const safeIssuer = encodeURIComponent(issuer);
  if (accountName) {
    return setupDetails.getSetupUri(safeIssuer, encodeURIComponent(accountName)).toString();
  }
  return setupDetails.getSetupUri(safeIssuer).toString();
};

const resolveAuthenticatorAccountLabel = async (fallbackLabel = '') => {
  if (fallbackLabel && !/^[0-9a-f-]{36}$/i.test(fallbackLabel)) {
    return fallbackLabel;
  }
  try {
    const attributes = await fetchUserAttributes();
    const preferred = attributes && attributes.preferred_username;
    if (preferred) return preferred;
    const raw = attributes && attributes['custom:data'];
    if (raw) {
      const data = JSON.parse(raw);
      if (data && data.CustomUserName) return data.CustomUserName;
    }
  } catch (err) {
    // Ignore attribute lookup failures and fall back to the provided label.
  }
  return fallbackLabel;
};

const withTimeout = (promise, ms, label) => Promise.race([
  promise,
  new Promise((_resolve, reject) => {
    setTimeout(
      () => {
        const timeoutError = new Error(`${label} timed out`);
        timeoutError.name = 'TimeoutError';
        reject(timeoutError);
      },
      ms
    );
  }),
]);

export const buildSessionTokens = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;
  const accessToken = session.tokens?.accessToken;

  return {
    accessToken: {
      jwtToken: accessToken?.toString() || '',
    },
    idToken: {
      jwtToken: idToken?.toString() || '',
      payload: idToken?.payload || {},
    },
    refreshToken: {
      token: 'managed-by-amplify',
    },
  };
};

export const classifySignInResult = (result) => {
  if (result && result.isSignedIn === true) {
    return { token: true };
  }

  const nextStep = (result && result.nextStep) || {};
  const { signInStep } = nextStep;
  const challengeParams = result && result.challengeParameters;
  const destination = (nextStep.codeDeliveryDetails
    && nextStep.codeDeliveryDetails.destination)
    || (challengeParams && challengeParams.CODE_DELIVERY_DESTINATION);

  switch (signInStep) {
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { challenge: { type: 'TOTP', signInStep } };
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { challenge: { type: 'EMAIL', signInStep, destination } };
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
      return {
        mfaSelection: {
          allowedMFATypes: Array.isArray(nextStep.allowedMFATypes)
            ? nextStep.allowedMFATypes
            : [],
        },
      };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
      const details = nextStep.totpSetupDetails;
      const accountName = challengeParams && challengeParams.USER_ID_FOR_SRP;
      const setupUri = details
        ? buildTotpSetupUri(details, authenticatorIssuer, accountName)
        : '';
      return {
        totpSetup: {
          sharedSecret: details ? details.sharedSecret : '',
          setupUri,
        },
      };
    }
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { error: 'New password required' };
    default:
      return { error: 'Unrecognized sign-in step', signInStep };
  }
};

export const awsLoginRequest = async (action) => {
  const { username, password } = action.payload;

  await restoreTrustedDeviceAsync({ active: amplifyKeyValueStorage });

  let result;
  try {
    result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
  } catch (err) {
    if (err.name === 'UserAlreadyAuthenticatedException') {
      await amplifySignOut();
      result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
    } else {
      throw err;
    }
  }
  if (result && result.isSignedIn) {
    return buildSessionTokens();
  }

  return classifySignInResult(result);
};

export const completeNewPassword = async (newPassword) => {
  // Default 30s bound for a Cognito call (Req 12.7).
  const result = await withTimeout(
    confirmSignIn({ challengeResponse: newPassword }),
    30000,
    'confirmSignIn'
  );

  if (result.isSignedIn) {
    return result;
  }

  throw new Error('Failed to set new password');
};

export const refreshToken = async () => {
  if (sessionStorage.getItem('token')) {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken;
      const accessToken = session.tokens?.accessToken;

      const tokens = {
        accessToken: accessToken?.toString() || '',
        idToken: idToken?.toString() || '',
        refreshToken: 'managed-by-amplify',
      };

      sessionStorage.setItem('token', tokens.accessToken);
      sessionStorage.setItem('idToken', tokens.idToken);
      sessionStorage.setItem('refreshToken', tokens.refreshToken);
      sessionStorage.setItem('expiryTime', String(Date.now() + (3600 * 1000)));
      sessionStorage.setItem('isTokenRefreshing', 'false');

      return tokens;
    } catch (err) {
      sessionStorage.setItem('isTokenRefreshing', 'false');
      throw err;
    }
  }
};

export const signOut = async () => {
  try {
    await amplifySignOut();
  } catch (err) {
    console.log('Sign out error:', err);
  }
};

export const signUp = (action) => {
  const { username, password, attributes, clientMetadata } = action.payload;

  return amplifySignUp({
    username,
    password,
    options: {
      userAttributes: attributes || {},
      clientMetadata: clientMetadata || {},
    },
  });
};

export const resendSignUp = (username, clientMetadata) => resendSignUpCode({
  username,
  options: {
    clientMetadata: clientMetadata || {},
  },
});

export const changePassword = async (action) => {
  await withTimeout(
    updatePassword({
      oldPassword: action.currentpassword,
      newPassword: action.password,
    }),
    30000,
    'updatePassword'
  );
  return 'SUCCESS';
};

export const confirmSignUpCode = ({ username, code, clientMetadata }) => amplifyConfirmSignUp({
  username,
  confirmationCode: code,
  options: clientMetadata ? { clientMetadata } : undefined,
});

export const signInAfterConfirm = async (username, password, accountLabel = '') => {
  let result;
  try {
    result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
  } catch (err) {
    if (err?.name === 'UserAlreadyAuthenticatedException') {
      await amplifySignOut();
      result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
    } else {
      throw err;
    }
  }

  if (result && result.isSignedIn) {
    return { status: 'SIGNED_IN' };
  }
  const signInStep = result && result.nextStep && result.nextStep.signInStep;
  if (signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
    const details = result.nextStep.totpSetupDetails;
    const accountName = await resolveAuthenticatorAccountLabel(accountLabel);
    const setupUri = buildTotpSetupUri(details, getAuthenticatorIssuer(), accountName);
    return {
      status: 'TOTP_SETUP_REQUIRED',
      sharedSecret: details.sharedSecret,
      setupUri,
    };
  }

  return { status: 'FALLBACK', signInStep };
};

let totpSetupInFlight = null;

const runStartTotpSetup = async () => {
  const session = await fetchAuthSession();
  if (!session || !session.tokens || !session.tokens.accessToken) {
    const err = new Error('You need to be signed in to set up an authenticator app. Please sign in and try again.');
    err.name = 'NotAuthenticated';
    throw err;
  }

  const output = await withTimeout(setUpTOTP(), 10000, 'setUpTOTP');
  const accountName = await resolveAuthenticatorAccountLabel();
  const setupUri = buildTotpSetupUri(output, getAuthenticatorIssuer(), accountName);
  return {
    sharedSecret: output.sharedSecret,
    setupUri,
  };
};

export const startTotpSetup = () => {
  if (totpSetupInFlight) {
    return totpSetupInFlight;
  }
  totpSetupInFlight = runStartTotpSetup().finally(() => {
    totpSetupInFlight = null;
  });
  return totpSetupInFlight;
};

export const confirmTotpSetup = async (code) => {
  await withTimeout(verifyTOTPSetup({ code }), 30000, 'verifyTOTPSetup');
  await withTimeout(
    updateMFAPreference({ totp: 'ENABLED' }),
    30000,
    'updateMFAPreference'
  );
  return 'SUCCESS';
};

export const confirmTotpSetupViaSignIn = async (code) => {
  const result = await withTimeout(
    confirmSignIn({ challengeResponse: code }),
    30000,
    'confirmSignIn'
  );

  if (result && result.isSignedIn) {
    await withTimeout(
      updateMFAPreference({ totp: 'ENABLED' }),
      30000,
      'updateMFAPreference'
    );
    return buildSessionTokens();
  }

  throw new Error('Could not complete authenticator setup. Please sign in again.');
};

export const submitSignInChallenge = async (code) => {
  const result = await withTimeout(
    confirmSignIn({ challengeResponse: code }),
    30000,
    'confirmSignIn'
  );

  if (result.isSignedIn) {
    return buildSessionTokens();
  }

  return { nextStep: result.nextStep };
};

export const selectMfaMethod = async (mfaType) => {
  const result = await withTimeout(
    confirmSignIn({ challengeResponse: mfaType }),
    30000,
    'confirmSignIn'
  );
  return classifySignInResult(result);
};

export const restartSignInWithMethod = async (username, password, mfaType) => {
  let result;
  try {
    result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
  } catch (err) {
    if (err?.name === 'UserAlreadyAuthenticatedException') {
      await amplifySignOut();
      result = await withTimeout(signIn({ username, password }), 10000, 'signIn');
    } else {
      throw err;
    }
  }

  const signInStep = result && result.nextStep && result.nextStep.signInStep;

  if (signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
    const selected = await withTimeout(
      confirmSignIn({ challengeResponse: mfaType }),
      30000,
      'confirmSignIn'
    );
    return classifySignInResult(selected);
  }

  if (
    (mfaType === 'EMAIL' && signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE')
    || (mfaType === 'TOTP' && signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE')
  ) {
    return classifySignInResult(result);
  }

  return { error: 'SWITCH_NOT_AVAILABLE' };
};

export const enableEmailMfa = async () => {
  await withTimeout(
    updateMFAPreference({ email: 'ENABLED' }),
    10000,
    'updateMFAPreference'
  );
  return 'SUCCESS';
};

export const isMfaRequired = async () => {
  try {
    const attributes = await withTimeout(
      fetchUserAttributes(),
      5000,
      'fetchUserAttributes'
    );
    const raw = attributes && attributes['custom:data'];
    if (!raw) return false;
    const data = JSON.parse(raw);
    return data.IsMFARequired === true || data.IsMFARequired === 'true';
  } catch (err) {
    return false;
  }
};

export const getMfaStatus = async () => {
  const pref = await withTimeout(fetchMFAPreference(), 5000, 'fetchMFAPreference');
  const enabled = Array.isArray(pref?.enabled) && pref.enabled.length > 0;
  return {
    enabled,
    preferred: pref?.preferred || null,
    raw: pref,
  };
};

export const rememberThisDevice = async () => {
  try {
    await rememberDevice();
    persistTrustedDevice();
    return true;
  } catch (err) {
    console.warn('rememberDevice failed (device tracking may be disabled):', err?.message);
    return false;
  }
};

export const isValidCode = (str) => typeof str === 'string' && /^[0-9]{6}$/.test(str);

export const mapContract = (tokens) => {
  const safeTokens = tokens || {};
  const payload = (safeTokens.idToken && safeTokens.idToken.payload) || {};

  return {
    token: (safeTokens.accessToken && safeTokens.accessToken.jwtToken) || '',
    idToken: (safeTokens.idToken && safeTokens.idToken.jwtToken) || '',
    refreshToken: 'managed-by-amplify',
    'user.username': payload.preferred_username || '',
    'user.firstName': payload['custom:first_name'] || '',
    'user.lastName': payload['custom:last_name'] || '',
    expiryTime: String(Date.now() + (3600 * 1000)),
    isTokenRefreshing: 'false',
  };
};

const LAMBDA_VALIDATION_PREFIX = /^[A-Za-z]+ failed with error\s+/;
export const resolveErrorMessage = (
  err,
  fallback = 'The operation failed. Please try again.'
) => {
  let message = err && err.message;
  const isLambdaValidation = !!err
    && (err.code === 'UserLambdaValidationException'
      || err.name === 'UserLambdaValidationException');

  if (isLambdaValidation && typeof message === 'string') {
    message = message.replace(LAMBDA_VALIDATION_PREFIX, '');
  }

  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  return fallback;
};

export const buildSignUpRequest = (username, email, ssn) => {
  const lowerUsername = typeof username === 'string' ? username.toLowerCase() : '';

  return {
    attributes: {
      email,
      'custom:data': JSON.stringify({ CustomUserName: lowerUsername }),
    },
    clientMetadata: {
      ssn,
      preferred_username: lowerUsername,
    },
  };
};

export const resolveMfaRoute = (status, mfaRequired = false) => {
  const enabled = !!(status && status.enabled);

  if (enabled) {
    return { route: '/consumer/dashboard', mfaSatisfied: 'true' };
  }
  if (mfaRequired) {
    return { route: '/two-factor/setup', mfaSatisfied: 'false' };
  }
  return { route: '/two-factor/offer', mfaSatisfied: 'true' };
};

export const mfaGuardRedirect = (idToken, flag) => {
  if (!idToken) {
    return { redirect: '/' };
  }
  if (flag !== 'true') {
    return { redirect: '/two-factor/setup' };
  }
  return { redirect: null };
};

export const AUTH_FLOW_FLAG = 'authFlowStep';
export const AUTH_STEP_CHALLENGE = 'challenge';
export const AUTH_STEP_SETUP = 'setup';

export const startAuthFlow = (step = AUTH_STEP_CHALLENGE) => {
  sessionStorage.setItem(AUTH_FLOW_FLAG, step);
};

export const clearAuthFlow = () => {
  sessionStorage.removeItem(AUTH_FLOW_FLAG);
};

export const twoFactorGuardRedirect = (idToken, step, allowedSteps = []) => {
  if (idToken) {
    return { redirect: null };
  }
  if (step && (allowedSteps.length === 0 || allowedSteps.includes(step))) {
    return { redirect: null };
  }
  return { redirect: '/' };
};

export const evaluateBlocks = (customerDetail, loansList) => {
  const primaryAddressState = customerDetail
    && customerDetail.primaryAddress
    && customerDetail.primaryAddress.state;
  const mailAddressState = customerDetail
    && customerDetail.mailAddress
    && customerDetail.mailAddress.state;

  if (primaryAddressState === 'MA' || mailAddressState === 'MA') {
    return { blocked: true, reason: 'MA' };
  }

  if (!loansList || loansList.length === 0) {
    return { blocked: true, reason: 'NO_LOANS' };
  }

  return { blocked: false, persist: true };
};
